import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { usePermissions } from '../../../context/PermissionContext';
import { AFF_FEATURE, AFF_EXPORT_OP, AFF_SIMULATE_OP } from '../../../shared/affPermissions';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { createAffAircraftConfig, createAffTracksConfig } from './affConfigs';
import { affService } from '../../../services/affService';
import { AffAircraftEntity } from './affServiceAdapter';
import {
  dispatchAffShowAircraft,
  dispatchAffClearTracks,
  AFF_SHOW_TRACK_EVENT,
  AFF_LOCATE_POINT_EVENT,
} from './affTrackEvent';
import './AffExplorer.scss';

type AffContext = 'aircraft' | 'tracks';

const CONTEXTS: { id: AffContext; label: string }[] = [
  { id: 'aircraft', label: 'Aircraft' },
  { id: 'tracks', label: 'Tracks' },
];

interface AffExplorerProps {
  className?: string;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
  onClose?: () => void;
  isWindowed?: boolean;
  windowTitle?: string;
  resizable?: boolean;
  draggable?: boolean;
  initialContext?: AffContext;
}

/**
 * Open the aircraft behind a track row. Fetched by esn rather than built from
 * the row, which has no make, model, owner or function to show.
 */
const showAircraftByEsn = async (esn: string, includeSim: boolean) => {
  try {
    const aircraft = await affService.fetchAircraftByEsn(esn, includeSim);
    if (aircraft) {
      dispatchAffShowAircraft(aircraft, { level: 'detail' });
    } else {
      // Better to say so than to open a modal full of blanks.
      console.warn(`[AffExplorer] no aircraft record found for esn ${esn}`);
    }
  } catch (error) {
    console.error('[AffExplorer] failed to load aircraft', error);
  }
};

/**
 * One explorer, two contexts.
 *
 * Aircraft reads aff_positions (one row per tail, current position); Tracks
 * reads aff_tracks (position history over a timeframe). They are separate CBE
 * entities sharing a shell, rather than a drill-down tab, so that Tracks is a
 * first-class grid with its own filters and its own export (#779).
 */
const AffExplorer: React.FC<AffExplorerProps> = (props) => {
  const { isFetchingUserInfo, isLoggedIn } = useFusionAuth();
  const { canDo } = usePermissions();
  const canExport = canDo(AFF_FEATURE, AFF_EXPORT_OP);
  // Simulated aircraft stay out of the grids unless a simulator operator asks
  // to see them, to test the Explorer itself. Exports never include them.
  const canSimulate = canDo(AFF_FEATURE, AFF_SIMULATE_OP);
  const [showSim, setShowSim] = useState(false);
  const includeSim = canSimulate && showSim;
  // #806 — Tracks is the context reached most often; opening on Aircraft cost a
  // click every time. An explicitly requested context still wins.
  const [context, setContext] = useState<AffContext>(props.initialContext ?? 'tracks');

  // The CBE hands its minimise handle back through this ref.
  const minimizeRef = useRef<(() => void) | null>(null);

  /**
   * The modals themselves live in AffAircraftModalHost, outside the explorer,
   * so a click on the aircraft's map marker reaches the same detail view. The
   * explorer only needs to get out of the way when something is put on the map
   * — a grid covering the map the user just asked to look at is no use.
   */
  useEffect(() => {
    const handler = () => minimizeRef.current?.();
    for (const event of [AFF_SHOW_TRACK_EVENT, AFF_LOCATE_POINT_EVENT]) {
      window.addEventListener(event, handler);
    }
    return () => {
      for (const event of [AFF_SHOW_TRACK_EVENT, AFF_LOCATE_POINT_EVENT]) {
        window.removeEventListener(event, handler);
      }
    };
  }, []);

  /**
   * #866 U3 — built once per context, not on every render. The CBE's fetch
   * callback depends on the config's arrays, and its 60 s poll re-arms whenever
   * that callback changes, so a fresh config per render restarted the interval
   * (and rebuilt the grid columns) on every re-render of the host.
   */
  const config = useMemo(
    () =>
      context === 'aircraft'
        ? createAffAircraftConfig({ canExport, includeSim })
        : createAffTracksConfig({ onShowAircraft: (esn) => showAircraftByEsn(esn, includeSim), canExport, includeSim }),
    [context, canExport, includeSim],
  );

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const defaultPosition = isMobile ? { x: 5, y: 5 } : { x: 150, y: 120 };
  const defaultSize = isMobile
    ? { width: window.innerWidth - 10, height: window.innerHeight - 10 }
    : { width: 1300, height: 700 };

  if (isFetchingUserInfo) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading user context...</div>;
  }

  const switcher = (
    <div className="aff-context-switcher" role="tablist" aria-label="AFF context">
      {CONTEXTS.map((c) => (
        <button
          key={c.id}
          type="button"
          role="tab"
          aria-selected={context === c.id}
          className={`aff-context-tab ${context === c.id ? 'is-active' : ''}`}
          onClick={() => setContext(c.id)}
        >
          {c.label}
        </button>
      ))}
      <button
        type="button"
        className="aff-clear-tracks"
        // Available from both contexts: a track can be left on the map from
        // either, so the way to remove it must not be hidden behind the one
        // the user happens not to be in.
        onClick={() => dispatchAffClearTracks()}
        title="Remove all aircraft tracks from the map"
      >
        Clear Tracks
      </button>
      {canSimulate && (
        <label className="aff-show-sim" title="Include simulated (SIM) aircraft in these grids. Exports never include them.">
          <input type="checkbox" checked={showSim} onChange={(e) => setShowSim(e.target.checked)} />
          Show simulated
        </label>
      )}
      <ContextHelp title="Help with AFF Data" context="aff-help" />
    </div>
  );

  const explorer = (
    <ConfigurableBaseExplorer<any>
      // Remounting on switch is deliberate: the two contexts have different
      // filters, and a stale esn filter surviving into the Aircraft grid would
      // silently show the wrong rows.
      key={`aff-explorer-${context}-${includeSim}`}
      config={config}
      minimizeRef={minimizeRef}
      isAuthenticated={isLoggedIn}
      isWindowed={props.isWindowed ?? true}
      initialPosition={props.initialPosition ?? defaultPosition}
      initialSize={props.initialSize ?? defaultSize}
      resizable={props.resizable ?? true}
      draggable={props.draggable ?? true}
      showCloseButton={true}
      showMinimizeButton={true}
      windowTitle={props.windowTitle || 'AFF Explorer'}
      onWindowClose={props.onClose}
      className={`aff-explorer ${props.className || ''}`}
      customToolbar={switcher}
      // Aircraft only. A track row is a single position reading — there is
      // nothing to drill into, so Tracks gets no Actions column.
      onEntityView={
        context === 'aircraft'
          ? (entity: AffAircraftEntity) => dispatchAffShowAircraft(entity, { level: 'detail' })
          : undefined
      }
    />
  );

  return explorer;
};

export default AffExplorer;
